import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { QuoteStatus } from '../../../models/store/quoteStatus';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { saveSession } from '../../../services/session/save';
import { getQuotePageFlag, isQuotePageVariant } from '@domgen/shared/enums/featureFlags';
import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';

/**
 * Stores quote response data and computed offer details for quote/confirmation views.
 *
 * Chooses payment method preference for display values, navigates to quote,
 * and persists the updated context to session storage.
 *
 * @param context Current store context.
 * @param event Event containing quote response data.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with quote, confirmation offer data, and GA fields.
 */
export const setQuote = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: QuoteResponse },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: 'setQuote', data: event.data });
  const quote = event.data;

  if (!quote?.offer?.contracts || quote.offer.contracts.length === 0) {
    log.error({ message: 'No contracts found in quote', data: quote });
    enqueue.emit.navigate({ path: AppRoute.ERROR });
    return context;
  }

  const applianceCare = quote?.offer?.contracts[0]?.contractAttributes.monthly_subs;

  const offerData = {
    applianceCare,
    startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
    endDate: quote?.offer.contracts[0]?.contractAttributes.contractEndDate,
    netFirstPaymentAmount:
      applianceCare?.cc?.netFirstPaymentAmount || applianceCare?.ach?.netFirstPaymentAmount,
    netFirstPaymentAmountAfterPromotionalPeriod:
      applianceCare?.cc?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod ||
      applianceCare?.ach?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod,
    discountCode: applianceCare
      ? [
          applianceCare.cc?.discountProfileAttributes?.ccDiscountCodeApplied,
          applianceCare.ach?.discountProfileAttributes?.achDiscountCodeApplied,
        ]
          .filter((code): code is string => !!code && code.toString().trim().length > 0)
          .join(' | ')
      : '',
    offerReference: quote?.offer.offerReference,
  };

  log.debug({ message: 'Computed offer data for quote', data: offerData });

  // Add quote page experiment tracking to GA event data and pageviews for all events
  // after it has been called/initialised. This is also the call that reports the
  // PostHog exposure ($feature_flag_called), so it must stay on the path every
  // quoted user takes, and only once per quote.
  const quotePageFlag = getQuotePageFlag(import.meta.env.VITE_BRAND_CODE);
  const flagResponse = quotePageFlag ? posthog.getFeatureFlag(quotePageFlag) : undefined;
  const quoteVariant = isQuotePageVariant(flagResponse) ? flagResponse : undefined;

  if (flagResponse && !quoteVariant) {
    log.warn({
      message:
        'Unrecognised quote page experiment variant from PostHog, falling back to the control layout',
      data: { flag: quotePageFlag, response: flagResponse },
    });
  }

  enqueue.emit.navigate({ path: AppRoute.QUOTE });
  const updatedContext = {
    ...context,
    quote: {
      status: QuoteStatus.READY,
      data: quote,
    },
    confirmation: {
      ...context.confirmation,
      applianceDetails: {
        ...context.confirmation?.applianceDetails,
        guarantee: quote?.items?.[0]?.guarantee ?? 12, // default to 12 months if guarantee length is not available from quote response
      },
      offerData,
    },
    gaEventData: {
      ...context.gaEventData,
      netFirstPaymentAmount: offerData.netFirstPaymentAmount,
      experimentName: quoteVariant ? quotePageFlag : undefined,
      experimentVariant: quoteVariant,
    },
  };

  saveSession(updatedContext);

  return updatedContext;
};
